"use client";

import React, { useState } from "react";
import { Button } from "./button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./card";
import { Input } from "./input";
import { Label } from "./label";
import { Slack, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
import { joinPartnerEcosystem } from "../lib/partner-ecosystem";
import { toast } from "sonner";

export interface PartnerSlackCTAProps {
  variant?: "card" | "banner" | "button";
  size?: "sm" | "md" | "lg";
  className?: string;
  onOpen?: () => void;
  showForm?: boolean; // If true, shows email/name form instead of direct link
}

const PARTNER_ECOSYSTEM_DESCRIPTION =
  "Connect with others working on and selling West Tennessee Consulting, chat about opportunities, ask questions from the team, and more.";

/**
 * PartnerSlackCTA Component
 *
 * A reusable call-to-action component for joining the WTC partner ecosystem on Slack.
 * Supports multiple variants (card, banner, button) for flexible integration across apps.
 *
 * @example
 * // Card variant (default)
 * <PartnerSlackCTA />
 *
 * // Banner variant with form
 * <PartnerSlackCTA variant="banner" showForm />
 *
 * // Button variant
 * <PartnerSlackCTA variant="button" size="lg" />
 */
export function PartnerSlackCTA({
  variant = "card",
  size = "md",
  className,
  onOpen,
  showForm = true,
}: PartnerSlackCTAProps) {
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    onOpen?.();
    if (showForm) {
      setShowModal(true);
    } else {
      // Direct link (legacy behavior)
      window.open(
        "https://join.slack.com/t/wtc-partners/shared_invite/zt-3o8hhd9z4-FG6r41s3oJZYU1ibtFsOlw",
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !name) {
      toast.error("Please enter both email and name");
      return;
    }

    setIsLoading(true);
    try {
      const result = await joinPartnerEcosystem({ email, name });
      
      if (result.success) {
        toast.success(result.message || "Welcome to the partner ecosystem!");
        setEmail("");
        setName("");
        setShowModal(false);
      } else {
        toast.error(result.error || "Failed to join");
      }
    } catch (error) {
      toast.error("An error occurred");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Modal for email/name form
  if (showModal && showForm) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Slack className="h-5 w-5 text-blue-600" />
              <CardTitle>Join the Partner Ecosystem</CardTitle>
            </div>
            <CardDescription>
              Enter your details to receive the Slack invite link
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 gap-2"
                >
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isLoading ? "Joining..." : "Join Slack"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (variant === "button") {
    return (
      <Button
        onClick={handleClick}
        size={size === "sm" ? "sm" : size === "lg" ? "lg" : "default"}
        className={cn("gap-2", className)}
        disabled={isLoading}
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        <Slack className="h-4 w-4" />
        Join Partner Ecosystem
      </Button>
    );
  }

  if (variant === "banner") {
    return (
      <div
        className={cn(
          "w-full bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 sm:p-6",
          className
        )}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex-shrink-0 mt-1">
              <Slack className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Join the Partner Ecosystem
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {PARTNER_ECOSYSTEM_DESCRIPTION}
              </p>
            </div>
          </div>
          <Button
            onClick={handleClick}
            size="sm"
            className="flex-shrink-0 gap-2"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            <Slack className="h-4 w-4" />
            Join on Slack
          </Button>
        </div>
      </div>
    );
  }

  // Default: card variant
  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex-shrink-0 mt-1">
              <Slack className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">Join the Partner Ecosystem</CardTitle>
              <CardDescription className="mt-2">
                {PARTNER_ECOSYSTEM_DESCRIPTION}
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Button
          onClick={handleClick}
          size={size === "sm" ? "sm" : size === "lg" ? "lg" : "default"}
          className="w-full sm:w-auto gap-2"
          disabled={isLoading}
        >
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          <Slack className="h-4 w-4" />
          Join on Slack
        </Button>
      </CardContent>
    </Card>
  );
}
